/**
 * Build a Babylon material for a scene object.
 * Supports:
 *   • Diffuse (asset_url or textures.diffuse) — PNG alpha channel auto-detected via `hasAlpha`.
 *   • Alpha modes: 'opaque' | 'cutout' | 'blend' (mapped to Babylon transparencyMode).
 *   • Normal / Bump map (textures.normal or textures.bump)
 *   • Ambient Occlusion (textures.ao) — ambientTexture, mapped as occlusion
 *   • Emissive (textures.emissive)
 *   • Metallic (textures.metallic) + Roughness (textures.roughness) — auto-switches to PBRMaterial
 *   • UV controls: uv.uScale, vScale, uOffset, vOffset (tiling & offset)
 * Any missing map is skipped. Backward compatible with the old `asset_url + color` schema.
 */
export function buildObjectMaterial(BABYLON, scene, obj, fallbackColor) {
  const tex = obj.textures || {};
  const diffuseUrl = tex.diffuse || obj.asset_url;
  const normalUrl  = tex.normal  || tex.bump || obj.normal_url;
  const aoUrl      = tex.ao      || tex.occlusion || obj.ao_url;
  const emissiveUrl= tex.emissive || obj.emissive_url;
  const metallicUrl= tex.metallic || obj.metallic_url;
  const roughnessUrl = tex.roughness || obj.roughness_url;

  const uv = obj.uv || {};
  const uScale = Number.isFinite(uv.uScale) ? uv.uScale : 1;
  const vScale = Number.isFinite(uv.vScale) ? uv.vScale : 1;
  const uOffset = Number.isFinite(uv.uOffset) ? uv.uOffset : 0;
  const vOffset = Number.isFinite(uv.vOffset) ? uv.vOffset : 0;

  const applyUV = (t) => {
    if (!t) return;
    t.uScale = uScale; t.vScale = vScale;
    t.uOffset = uOffset; t.vOffset = vOffset;
  };
  const mkTex = (url, invertY = true, hasAlpha = false) => {
    if (!url) return null;
    // Babylon flips textures on Y by default; passing `invertY=true` here means the resulting
    // texture appears upright on planes and box faces (matching how the source image is viewed).
    const t = new BABYLON.Texture(url, scene, false, invertY);
    if (hasAlpha) t.hasAlpha = true;
    applyUV(t);
    return t;
  };

  // Alpha mode
  const alphaMode = (obj.material?.alpha_mode || obj.alpha_mode || 'auto').toLowerCase();
  const wantsAlpha = alphaMode !== 'opaque';

  const usePBR = !!(obj.material?.pbr || metallicUrl || roughnessUrl || obj.material?.metallic !== undefined);

  const matName = `${obj.name || 'obj'}_mat`;
  let material;

  if (usePBR) {
    material = new BABYLON.PBRMaterial(matName, scene);
    material.albedoTexture = mkTex(diffuseUrl, true, wantsAlpha);
    if (!diffuseUrl && fallbackColor) {
      material.albedoColor = BABYLON.Color3.FromHexString(fallbackColor);
    }
    material.bumpTexture = mkTex(normalUrl);
    if (material.bumpTexture) material.invertNormalMapX = false;
    material.ambientTexture = mkTex(aoUrl);
    if (material.ambientTexture) material.useAmbientInGrayScale = true;
    material.emissiveTexture = mkTex(emissiveUrl);
    if (material.emissiveTexture) material.emissiveColor = new BABYLON.Color3(1, 1, 1);
    material.metallicTexture = mkTex(metallicUrl);
    if (material.metallicTexture && roughnessUrl && roughnessUrl === metallicUrl) {
      material.useRoughnessFromMetallicTextureAlpha = false;
      material.useRoughnessFromMetallicTextureGreen = true;
      material.useMetallnessFromMetallicTextureBlue = true;
    }
    material.metallic = Number.isFinite(obj.material?.metallic) ? obj.material.metallic : (metallicUrl ? 1 : 0);
    material.roughness = Number.isFinite(obj.material?.roughness) ? obj.material.roughness : (roughnessUrl ? 1 : 0.5);
  } else {
    material = new BABYLON.StandardMaterial(matName, scene);
    if (diffuseUrl) {
      material.diffuseTexture = mkTex(diffuseUrl, true, wantsAlpha);
      material.emissiveColor = new BABYLON.Color3(0.12, 0.12, 0.12);
    } else if (fallbackColor) {
      material.diffuseColor = BABYLON.Color3.FromHexString(fallbackColor);
      material.specularColor = new BABYLON.Color3(0.1, 0.1, 0.1);
    }
    material.bumpTexture = mkTex(normalUrl);
    material.ambientTexture = mkTex(aoUrl);
    material.emissiveTexture = mkTex(emissiveUrl);
    if (material.emissiveTexture) material.emissiveColor = new BABYLON.Color3(1, 1, 1);
  }

  // Alpha / transparency handling
  if (wantsAlpha && diffuseUrl) {
    material.backFaceCulling = false;
    if (usePBR) {
      // PBR: transparencyMode 1 = ALPHATEST (cutout), 2 = ALPHABLEND
      material.transparencyMode = alphaMode === 'cutout' ? 1 : 2;
      material.useAlphaFromAlbedoTexture = true;
    } else {
      material.useAlphaFromDiffuseTexture = true;
      // StandardMaterial: use alpha test for cutout
      if (alphaMode === 'cutout') {
        material.alphaMode = BABYLON.Engine.ALPHA_COMBINE;
        // Push discard threshold via .alphaCutOff (0-1, default 0.4)
        material.alphaCutOff = Number.isFinite(obj.material?.alpha_cutoff) ? obj.material.alpha_cutoff : 0.4;
      }
    }
  }

  // Detect alpha state helper for the editor UI ("Alpha: detected/none")
  if (material.diffuseTexture || material.albedoTexture) {
    const primary = material.diffuseTexture || material.albedoTexture;
    if (primary && primary.hasAlpha !== undefined) {
      material._pmHasAlpha = wantsAlpha;
    }
  }

  return material;
}

/**
 * Return a compact snapshot of which texture channels the object provides,
 * so the UI can show "AO ✓ Normal ✓ Alpha ✗" tags to the user.
 */
export function detectMaterialChannels(obj) {
  const tex = obj.textures || {};
  return {
    diffuse:  !!(tex.diffuse || obj.asset_url),
    normal:   !!(tex.normal || tex.bump || obj.normal_url),
    ao:       !!(tex.ao || tex.occlusion || obj.ao_url),
    emissive: !!(tex.emissive || obj.emissive_url),
    metallic: !!(tex.metallic || obj.metallic_url),
    roughness:!!(tex.roughness || obj.roughness_url),
    alpha:    (obj.material?.alpha_mode || obj.alpha_mode || 'auto') !== 'opaque',
    uv_tiling: !!(obj.uv && (obj.uv.uScale !== 1 || obj.uv.vScale !== 1)),
    pbr:      !!(obj.material?.pbr || tex.metallic || tex.roughness || obj.metallic_url),
  };
}
